/**
 * Mock data for the self-serve Media Plan flow.
 * Activated when VITE_ENABLE_MOCK_GENERATION=true or ?mockGeneration=true
 *
 * Entity shapes aligned with the ER model (entity-relationship.md):
 *   BusinessProfile → Lead → MediaPlan → Campaign → Creative
 *
 * The API returns enriched/denormalized responses — each mock object includes
 * both stored entity fields and computed display data (audience analysis,
 * schedule optimization, projections). Field names match what the backend
 * API will return, with backward-compat aliases where old names were used.
 *
 * Note: Only DSP/SSP-measurable metrics are included (impressions, reach,
 * completion rate, CPM, frequency). Customer actions like store visits,
 * website clicks, and phone calls are NOT included as they cannot be
 * measured from CTV campaign data alone.
 */

// ── BusinessProfile (scraped from Google Places / website) ──────────────────

export const MOCK_BUSINESS_PROFILE = {
  // ── Entity fields ──
  id: 'bp-001',
  parentProfileId: null,
  placeId: 'mock-place-autozone-001',
  websiteUrl: 'https://autozone.com/store/downtown-denver',
  name: 'AutoZone Downtown',
  address: '742 E Colfax Ave, Denver, CO 80203',
  locationGeo: { lat: 39.7401, lng: -104.9733 },
  locationType: 'pin',

  // ── Denormalized display fields (from scraped_data) ──
  phone: '(303) 555-0189',
  category: 'Auto Parts Store',
  rating: 4.4,
  reviewsCount: 387,
  priceLevel: 2,
  openingHours: ['Mon-Sat 7:30am-9pm', 'Sun 8am-8pm'],
  logoUrl: null,
  photos: [
    { id: 'p1', url: null, caption: 'Storefront', source: 'google_maps' },
    { id: 'p2', url: null, caption: 'Interior aisle', source: 'google_maps' },
    { id: 'p3', url: null, caption: 'Auto parts display', source: 'google_maps' },
  ],

  // ── Backward-compat aliases ──
  website: 'https://autozone.com/store/downtown-denver',
  location: { lat: 39.7401, lng: -104.9733 },
};

/** @deprecated Use MOCK_BUSINESS_PROFILE */
export const MOCK_BUSINESS = MOCK_BUSINESS_PROFILE;

// ── Mock Reviews (extracted from listing) ───────────────────────────────────

export const MOCK_REVIEWS = [
  {
    id: 'r1',
    text: 'Knowledgeable staff helped me find exactly the right brake pads for my truck. In and out in 10 minutes.',
    author: 'Mike R.',
    rating: 5,
  },
  {
    id: 'r2',
    text: 'Great selection of hard-to-find parts. They had the alternator for my 2003 Silverado when nobody else did.',
    author: 'Carlos P.',
    rating: 5,
  },
  {
    id: 'r3',
    text: 'Fair prices and open weekends when I actually have time to work on my car. Staff is always friendly.',
    author: 'Jennifer K.',
    rating: 4,
  },
  {
    id: 'r4',
    text: 'The battery testing service saved me from buying a new battery I didn\'t need. Honest people here.',
    author: 'David W.',
    rating: 5,
  },
];

// ── Review-derived Differentiator Suggestions ───────────────────────────────

export const MOCK_DIFFERENTIATORS = [
  'Knowledgeable staff',
  'Hard-to-find parts',
  'Open weekends',
  'Fair prices',
  'Fast in-store pickup',
  'Free battery testing',
];

// ── MediaPlan Strategies (enriched API responses) ───────────────────────────

export const MOCK_PLANS = [
  {
    id: 'plan-001',

    // ── MediaPlan entity fields ──
    name: 'Drive Weekend Foot Traffic',
    status: 'proposed',
    budgetCents: 250000,
    budget: 2500,
    flightStart: '2026-03-09',
    flightEnd: '2026-04-05',
    targeting: {
      centerGeo: { lat: 39.7401, lng: -104.9733 },
      centerLabel: '742 E Colfax Ave, Denver, CO',
      radiusMiles: 5,
      geoType: 'radius',
    },
    targetProfileId: 'bp-001',

    // ── Campaign-derivable fields ──
    objective: 'foot_traffic',
    format: '15-sec CTV spot',
    variants: 2,

    // ── Computed display data ──
    recommended: true,
    duration: '4 weeks',
    audience: {
      radius: 5,
      households: 15000,
      segments: [
        { label: 'Local residents (5mi radius)', color: '#FF6B6B' },
        { label: 'DIY & Auto Enthusiasts', color: '#4ECDC4' },
        { label: 'Active Weekend Shoppers', color: '#7C3AED' },
        { label: 'Men 25-55, home project interest', color: '#F59E0B' },
      ],
    },
    insight: {
      text: 'Searches for "car repair" and "auto parts" near you jumped 30% this month -- mostly on Friday afternoons. Your neighbors are planning weekend projects, and your store can be the first thing they see.',
      highlightText: 'jumped 30%',
      source: 'Local search trends',
    },
    creative: {
      tone: ['Upbeat', 'Local', 'Helpful'],
      message: 'Professional parts for your weekend project.',
      format: '15-sec CTV spot',
      quality: 'HD',
    },
    schedule: {
      peak: 'Fri-Sun primetime',
      description: 'Runs heaviest Fri-Sun evenings, when your customers plan weekend projects.',
      heatmap: {
        morning:   [0.12, 0.12, 0.12, 0.12, 0.25, 0.25, 0.25],
        afternoon: [0.18, 0.18, 0.18, 0.18, 0.35, 0.45, 0.45],
        primetime: [0.30, 0.30, 0.30, 0.30, 0.55, 0.70, 0.70],
      },
    },
    projections: {
      impressions: { min: 85000, max: 110000 },
      completionRate: 95,
      reach: { min: 12000, max: 15000 },
      cpm: { min: 22, max: 29 },
      frequency: { min: 5.7, max: 7.3 },
    },

    // ── Backward-compat aliases ──
    startDate: '2026-03-09',
    endDate: '2026-04-05',
  },
  {
    id: 'plan-002',

    // ── MediaPlan entity fields ──
    name: 'Brand Awareness Boost',
    status: 'proposed',
    budgetCents: 180000,
    budget: 1800,
    flightStart: '2026-03-09',
    flightEnd: '2026-04-05',
    targeting: {
      centerGeo: { lat: 39.7401, lng: -104.9733 },
      centerLabel: '742 E Colfax Ave, Denver, CO',
      radiusMiles: 10,
      geoType: 'radius',
    },
    targetProfileId: 'bp-001',

    // ── Campaign-derivable fields ──
    objective: 'awareness',
    format: '15-sec CTV spot',
    variants: 1,

    // ── Computed display data ──
    recommended: false,
    duration: '4 weeks',
    audience: {
      radius: 10,
      households: 28000,
      segments: [
        { label: 'Local residents (10mi radius)', color: '#FF6B6B' },
        { label: 'Vehicle owners', color: '#4ECDC4' },
        { label: 'Home improvement interest', color: '#7C3AED' },
      ],
    },
    insight: {
      text: 'Brand recognition for local auto parts stores in your area is low -- only 22% of nearby residents could name a local option. This is an opportunity to establish your store as the go-to choice.',
      highlightText: 'only 22%',
      source: 'Market research data',
    },
    creative: {
      tone: ['Friendly', 'Local', 'Trustworthy'],
      message: 'Your neighborhood auto parts experts.',
      format: '15-sec CTV spot',
      quality: 'HD',
    },
    schedule: {
      peak: 'Evenings across the week',
      description: 'Distributed evenly across weekday and weekend evenings for maximum reach.',
      heatmap: {
        morning:   [0.10, 0.10, 0.10, 0.10, 0.10, 0.15, 0.15],
        afternoon: [0.15, 0.15, 0.15, 0.15, 0.15, 0.20, 0.20],
        primetime: [0.40, 0.40, 0.40, 0.40, 0.40, 0.45, 0.45],
      },
    },
    projections: {
      impressions: { min: 65000, max: 85000 },
      completionRate: 94,
      reach: { min: 20000, max: 26000 },
      cpm: { min: 21, max: 28 },
      frequency: { min: 3.3, max: 4.3 },
    },

    // ── Backward-compat aliases ──
    startDate: '2026-03-09',
    endDate: '2026-04-05',
  },
  {
    id: 'plan-003',

    // ── MediaPlan entity fields ──
    name: 'Spring Maintenance Push',
    status: 'proposed',
    budgetCents: 320000,
    budget: 3200,
    flightStart: '2026-03-16',
    flightEnd: '2026-04-12',
    targeting: {
      centerGeo: { lat: 39.7401, lng: -104.9733 },
      centerLabel: '742 E Colfax Ave, Denver, CO',
      radiusMiles: 7,
      geoType: 'radius',
    },
    targetProfileId: 'bp-001',

    // ── Campaign-derivable fields ──
    objective: 'promotion',
    format: '15-sec CTV spot',
    variants: 2,

    // ── Computed display data ──
    recommended: false,
    duration: '4 weeks',
    audience: {
      radius: 7,
      households: 21000,
      segments: [
        { label: 'Local residents (7mi radius)', color: '#FF6B6B' },
        { label: 'Vehicle maintenance seekers', color: '#4ECDC4' },
        { label: 'Spring home & auto project planners', color: '#7C3AED' },
        { label: 'Budget-conscious auto owners', color: '#F59E0B' },
      ],
    },
    insight: {
      text: 'Spring is the #1 season for auto maintenance -- oil changes, tire rotations, and brake inspections spike 45% in March-April. Capturing this demand early drives sustained foot traffic.',
      highlightText: 'spike 45%',
      source: 'Industry seasonal trends',
    },
    creative: {
      tone: ['Energetic', 'Seasonal', 'Value-driven'],
      message: 'Spring into savings on the parts you need.',
      format: '15-sec CTV spot',
      quality: 'HD',
    },
    schedule: {
      peak: 'Thu-Sun afternoons & evenings',
      description: 'Concentrated on late-week and weekend blocks when spring project planning peaks.',
      heatmap: {
        morning:   [0.08, 0.08, 0.08, 0.15, 0.20, 0.25, 0.25],
        afternoon: [0.12, 0.12, 0.12, 0.25, 0.40, 0.50, 0.50],
        primetime: [0.20, 0.20, 0.20, 0.35, 0.55, 0.65, 0.65],
      },
    },
    projections: {
      impressions: { min: 110000, max: 145000 },
      completionRate: 96,
      reach: { min: 16000, max: 20000 },
      cpm: { min: 22, max: 29 },
      frequency: { min: 5.5, max: 7.3 },
    },

    // ── Backward-compat aliases ──
    startDate: '2026-03-16',
    endDate: '2026-04-12',
  },
];

// ── Campaign (post-payment) ─────────────────────────────────────────────────

export const MOCK_CAMPAIGN = {
  // ── Campaign entity fields ──
  id: 'camp-001',
  mediaPlanId: 'plan-001',
  businessProfileId: 'bp-001',
  purpose: 'foot_traffic',
  tone: 'auto',
  channel: 'ctv',
  offers: [],
  notes: '',

  // ── Denormalized display fields ──
  planName: 'Drive Weekend Foot Traffic',
  businessName: 'AutoZone Downtown',
  status: 'live',
  budget: 2500,
  startDate: '2026-03-09',
  endDate: '2026-04-05',

  // ── Timestamps ──
  createdAt: '2026-03-03T14:30:00Z',
  paidAt: '2026-03-03T14:32:00Z',
  generatedAt: '2026-03-03T14:34:12Z',
  approvedAt: '2026-03-03T14:36:00Z',
  launchedAt: '2026-03-03T14:36:05Z',

  // ── Backward-compat alias ──
  planId: 'plan-001',
};

// ── Creatives (generated video outputs) ─────────────────────────────────────

export const MOCK_CREATIVES = [
  {
    id: 'crtv-001',
    campaignId: 'camp-001',
    label: 'Weekend',
    description: 'Optimized for Friday-Sunday evening viewers',
    state: 'completed',
    progressPercent: 100,
    videoUrl: null,
    thumbnailUrl: null,
    duration: 15,
    resolution: '1920x1080',
  },
  {
    id: 'crtv-002',
    campaignId: 'camp-001',
    label: 'Weekday',
    description: 'Optimized for Monday-Thursday primetime',
    state: 'completed',
    progressPercent: 100,
    videoUrl: null,
    thumbnailUrl: null,
    duration: 15,
    resolution: '1920x1080',
  },
];

/** @deprecated Use MOCK_CREATIVES */
export const MOCK_VARIANTS = MOCK_CREATIVES;

// ── Generation Steps ────────────────────────────────────────────────────────

export const MOCK_GENERATION_STEPS = [
  { id: 1, label: 'Analyzing your business', detail: 'Reviewing photos, reviews, and location data', duration: 3000 },
  { id: 2, label: 'Crafting your story', detail: 'Writing a script tailored to your audience', duration: 4000 },
  { id: 3, label: 'Selecting visuals', detail: 'Choosing the best imagery for your ad', duration: 3500 },
  { id: 4, label: 'Composing audio', detail: 'Generating voiceover and background music', duration: 4500 },
  { id: 5, label: 'Rendering your ad', detail: 'Assembling the final video', duration: 5000 },
  { id: 6, label: 'Quality check', detail: 'Ensuring everything looks great', duration: 2000 },
];

// ── Report Data (DSP/SSP-measurable metrics only) ───────────────────────────

export const MOCK_REPORT = {
  campaignId: 'camp-001',
  dateRange: { start: '2026-03-09', end: '2026-04-05' },
  status: 'completed',
  projections: {
    impressions: { min: 85000, max: 110000 },
    reach: { min: 12000, max: 15000 },
    completionRate: 95,
    cpm: { min: 22, max: 29 },
    frequency: { min: 5.7, max: 7.3 },
  },
  actuals: {
    impressions: 98420,
    reach: 14200,
    completionRate: 96.3,
    cpm: 25.40,
    frequency: 6.9,
  },
  timeline: [
    { date: '2026-03-09', impressions: 2100, reach: 1800 },
    { date: '2026-03-10', impressions: 2400, reach: 2050 },
    { date: '2026-03-11', impressions: 2200, reach: 1900 },
    { date: '2026-03-12', impressions: 2300, reach: 1950 },
    { date: '2026-03-13', impressions: 3100, reach: 2700 },
    { date: '2026-03-14', impressions: 4200, reach: 3500 },
    { date: '2026-03-15', impressions: 4400, reach: 3600 },
    { date: '2026-03-16', impressions: 2200, reach: 1850 },
    { date: '2026-03-17', impressions: 2500, reach: 2100 },
    { date: '2026-03-18', impressions: 2300, reach: 1950 },
    { date: '2026-03-19', impressions: 2400, reach: 2000 },
    { date: '2026-03-20', impressions: 3300, reach: 2800 },
    { date: '2026-03-21', impressions: 4500, reach: 3700 },
    { date: '2026-03-22', impressions: 4600, reach: 3750 },
    { date: '2026-03-23', impressions: 2350, reach: 1950 },
    { date: '2026-03-24', impressions: 2450, reach: 2050 },
    { date: '2026-03-25', impressions: 2350, reach: 1980 },
    { date: '2026-03-26', impressions: 2500, reach: 2100 },
    { date: '2026-03-27', impressions: 3400, reach: 2900 },
    { date: '2026-03-28', impressions: 4700, reach: 3850 },
    { date: '2026-03-29', impressions: 4800, reach: 3900 },
    { date: '2026-03-30', impressions: 2400, reach: 2000 },
    { date: '2026-03-31', impressions: 2550, reach: 2150 },
    { date: '2026-04-01', impressions: 2450, reach: 2050 },
    { date: '2026-04-02', impressions: 2600, reach: 2200 },
    { date: '2026-04-03', impressions: 3500, reach: 3000 },
    { date: '2026-04-04', impressions: 4900, reach: 4000 },
    { date: '2026-04-05', impressions: 5020, reach: 4100 },
  ],
  benchmarks: {
    industryAvgCompletion: 92,
    industryAvgCpm: 30,
    performanceVsIndustry: '+4.3%',
    completionVsIndustry: 'above average',
    cpmVsIndustry: '15% below average',
  },
};

// ── Dashboard Campaigns (denormalized list) ─────────────────────────────────

export const MOCK_DASHBOARD_CAMPAIGNS = [
  {
    id: 'camp-001',
    mediaPlanId: 'plan-001',
    businessProfileId: 'bp-001',
    name: 'Drive Weekend Foot Traffic',
    businessName: 'AutoZone Downtown',
    status: 'live',
    budget: 2500,
    startDate: '2026-03-09',
    endDate: '2026-04-05',
    impressions: 98420,
    reach: 14200,
    daysRemaining: 33,
    progress: 25,
  },
  {
    id: 'camp-002',
    mediaPlanId: 'plan-002',
    businessProfileId: 'bp-001',
    name: 'Holiday Battery Sale',
    businessName: 'AutoZone Downtown',
    status: 'completed',
    budget: 1500,
    startDate: '2025-12-01',
    endDate: '2025-12-28',
    impressions: 72300,
    reach: 11400,
    daysRemaining: 0,
    progress: 100,
  },
];

// ── Voice & Character Options ───────────────────────────────────────────────

export const MOCK_VOICE_OPTIONS = [
  { id: 'male', label: 'Male' },
  { id: 'female', label: 'Female' },
];

export const MOCK_CHARACTER_OPTIONS = [
  { id: 'none', label: 'None', description: 'No on-screen character' },
  { id: 'ambassador', label: 'Brand Ambassador', description: 'Friendly spokesperson for your brand' },
  { id: 'owner', label: 'Store Owner', description: 'Approachable business owner persona' },
  { id: 'customer', label: 'Customer', description: 'Happy customer testimonial style' },
  { id: 'spokesperson', label: 'Spokesperson', description: 'Professional presenter' },
];

// ── Helper: check if mock mode is enabled ───────────────────────────────────

export const isMockMode = () => {
  if (import.meta.env.VITE_ENABLE_MOCK_GENERATION === 'true') return true;
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mockGeneration') === 'true') return true;
  }
  return false;
};
